import { CarrierName, formatToDollar } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	type AdminAccountBondingLines,
	useAdminFetchAccountExposure,
} from "@portal/api/account/admin";
import {
	formatLimitCell,
	formatRateCell,
	limitToInputValue,
	parseLimitInput,
	parseRateInput,
	rateToInputValue,
} from "@portal/components/features/bonding-line/bondingLineFormatting";
import {
	CARRIER_ROW_NAMES,
	carrierDisplayName,
	INREV_KEY,
} from "@portal/components/features/bonding-line/carrierDisplay";
import { usedContractExposure } from "@portal/components/features/bonding-line/mapBondingLines";
import { Text } from "@portal/components/primitives";
import { Toggle } from "@portal/components/primitives/Toggle";
import { CompletionBar } from "@portal/components/ui/CompletionBar";
import {
	ColumnDef,
	DataGrid,
	DataGridColumnEntry,
	DataGridRow,
	EditableColumnDef,
} from "@portal/components/ui/DataGrid";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import { ReactNode, useCallback, useMemo } from "react";
import { formatEmpty } from "../../../../../../utils";
import { carrierLogoIconMap } from "../../../../../../utils/carrier";
import type { AccountIdentity } from "../../../../../shared/account/AccountHeader";
import { useBondingLineDispatch, useBondingLineState } from "./state/BondingLineStateProvider";
import {
	allCarriersMatched,
	BondingLineDraft,
	BondingLineState,
	BondingLineTarget,
	lineHasError,
	shouldShowLineErrors,
} from "./state/reducer";

type BondingLineRowData = {
	carrierName: CarrierName | typeof INREV_KEY;
	kind: "inrev" | "carrier";
};

type Row = DataGridRow<BondingLineRowData>;

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

const MatchLabel = ({ matched, children }: { matched: boolean; children: ReactNode }) => (
	<Text type="caption" color={matched ? "primary" : "tertiary"}>
		{children}
	</Text>
);

const targetForRow = (row: Row): BondingLineTarget =>
	row.data?.kind === "carrier"
		? { kind: "carrier", carrierName: row.data.carrierName }
		: { kind: "inrev" };

const draftForRow = (state: BondingLineState, row: Row): BondingLineDraft | undefined => {
	if (row.data?.kind === "inrev") return state.inrev;
	if (row.data?.kind === "carrier") return state.carriers[row.data.carrierName];
	return undefined;
};

const isCarrierLocked = (state: BondingLineState, row: Row): boolean =>
	row.data?.kind === "carrier" && (state.carriers[row.data.carrierName]?.match ?? false);

const NumericCell = ({ children }: { children: ReactNode }) => (
	<div
		className="flex-1 pr-3 text-right text-sm font-mono tabular-nums text-primary"
		data-amp-mask
		data-sentry-mask
	>
		{children}
	</div>
);

type BondingLinesGridProps = {
	account: Pick<AccountIdentity, "id">;
	bondingLines: Option<AdminAccountBondingLines>;
};

export const BondingLinesGrid = ({ account, bondingLines }: BondingLinesGridProps) => {
	const state = useBondingLineState();
	const dispatch = useBondingLineDispatch();
	const { exposure, exposureError } = useAdminFetchAccountExposure(account.id);

	const usedExposureForRow = useCallback(
		(row: Row): number | undefined => {
			if (row.data?.kind === "inrev") {
				if (!exposure) return undefined;
				return usedContractExposure(exposure);
			}
			const line = bondingLines?.carrierBondingLines.find(
				(carrier) => carrier.carrierName === row.data?.carrierName,
			);
			return line?.exposure.total ?? 0;
		},
		[bondingLines, exposure],
	);

	const columns = useMemo<DataGridColumnEntry<BondingLineRowData>[]>(() => {
		const editable = (row: Row) => state.mode !== "readonly" && !isCarrierLocked(state, row);

		const rateColumn: EditableColumnDef<BondingLineRowData> = {
			id: "rate",
			label: "Rate",
			width: "minmax(56px, 0.35fr)",
			navigable: true,
			editable,
			inputMode: "decimal",
			renderCell: (row) => (
				<NumericCell>{formatRateCell(draftForRow(state, row)?.rate ?? null)}</NumericCell>
			),
			getEditValue: (row) => rateToInputValue(draftForRow(state, row)?.rate ?? null),
			onCommit: (row, draft) => {
				const value = parseRateInput(draft);
				if (value === undefined) return;
				dispatch({ type: "set_rate", target: targetForRow(row), value });
			},
		};

		const singleColumn: EditableColumnDef<BondingLineRowData> = {
			id: "single",
			label: "Single Limit",
			width: "minmax(110px, 0.7fr)",
			navigable: true,
			editable,
			inputMode: "decimal",
			renderCell: (row) => (
				<NumericCell>{formatLimitCell(draftForRow(state, row)?.singleLimit ?? null)}</NumericCell>
			),
			getEditValue: (row) => limitToInputValue(draftForRow(state, row)?.singleLimit ?? null),
			onCommit: (row, draft) => {
				const value = parseLimitInput(draft);
				if (value === undefined) return;
				dispatch({ type: "set_single", target: targetForRow(row), value });
			},
		};

		const aggregateColumn: EditableColumnDef<BondingLineRowData> = {
			id: "aggregate",
			label: "Aggregate Limit",
			width: "minmax(110px, 0.7fr)",
			navigable: true,
			editable,
			inputMode: "decimal",
			renderCell: (row) => (
				<NumericCell>
					{formatLimitCell(draftForRow(state, row)?.aggregateLimit ?? null)}
				</NumericCell>
			),
			getEditValue: (row) => limitToInputValue(draftForRow(state, row)?.aggregateLimit ?? null),
			onCommit: (row, draft) => {
				const value = parseLimitInput(draft);
				if (value === undefined) return;
				dispatch({ type: "set_aggregate", target: targetForRow(row), value });
			},
		};

		const matchUtilColumn: ColumnDef<BondingLineRowData> = {
			id: "match-util",
			label: state.mode === "readonly" ? "Utilization" : "",
			width: "minmax(140px, 0.9fr)",
			navigable: false,
			renderCell: (row) => {
				if (state.mode === "readonly") {
					const draft = draftForRow(state, row);
					if (row.data?.kind === "inrev" && exposureError) {
						return (
							<div className="w-full px-3 py-1 text-right">
								<ErrorDisplay error={toError(exposureError)} />
							</div>
						);
					}
					return (
						<div className="w-full px-3 py-1 text-right">
							<CompletionBar
								value={usedExposureForRow(row)}
								max={draft?.aggregateLimit ?? 0}
								formatter={(value) =>
									formatEmpty(value === undefined ? null : formatToDollar(Math.round(value)))
								}
								barWrapperClassName="border-bc-default"
							/>
						</div>
					);
				}
				if (row.data?.kind === "inrev") {
					const allMatched = allCarriersMatched(state);
					return (
						<div className="px-3">
							<Toggle
								color="primary"
								size="sm"
								checked={allMatched}
								onChange={() => dispatch({ type: "match_all" })}
								data-testid="match-all-toggle"
								aria-label="Match all bonding lines to inRev"
								label={<MatchLabel matched={allMatched}>Match All</MatchLabel>}
							/>
						</div>
					);
				}
				const carrierName = row.data ? row.data.carrierName : "";
				const matched = state.carriers[carrierName]?.match ?? false;
				return (
					<div className="px-3">
						<Toggle
							color="primary"
							size="sm"
							checked={matched}
							onChange={() => dispatch({ type: "toggle_match", carrierName })}
							data-testid="auto-match-toggle"
							aria-label={`Match ${carrierDisplayName[carrierName] ?? carrierName} to inRev`}
							label={<MatchLabel matched={matched}>Match</MatchLabel>}
						/>
					</div>
				);
			},
		};

		return [rateColumn, singleColumn, aggregateColumn, matchUtilColumn];
	}, [state, dispatch, usedExposureForRow, exposureError]);

	const rows = useMemo<Row[]>(() => {
		const showErrors = shouldShowLineErrors(state);
		const inrevRow: Row = {
			id: INREV_KEY,
			label: carrierDisplayName[INREV_KEY],
			labelLeading: carrierLogoIconMap["inrev" as CarrierName],
			navigable: true,
			hasError: showErrors && lineHasError(state.inrev),
			data: { carrierName: INREV_KEY, kind: "inrev" },
		};
		const carrierRows: Row[] = CARRIER_ROW_NAMES.map((carrierName) => {
			const draft = state.carriers[carrierName];
			return {
				id: carrierName,
				label: carrierDisplayName[carrierName] ?? carrierName,
				labelLeading: carrierLogoIconMap[carrierName],
				navigable: true,
				hasError: showErrors && draft !== undefined && lineHasError(draft),
				data: { carrierName, kind: "carrier" },
			};
		});
		return [inrevRow, ...carrierRows];
	}, [state]);

	return (
		<DataGrid<BondingLineRowData>
			columns={columns}
			rows={rows}
			labelColumn={{
				label: "Line",
				width: "minmax(180px, 1fr)",
				indent: "compact",
				hideExpansionSpacer: true,
			}}
			gridLines="strong"
			headerHeight={40}
			testId="bonding-lines-grid"
		/>
	);
};
